import { configureStore, createAsyncThunk } from '@reduxjs/toolkit';
import { produceWithPatches } from 'immer';
import { UdViewModel } from '../../udView/udview';
import { startDrawingObjectListener } from './middleware/drawing-object-update-middleware';
import { listenerMiddleware } from './middleware/listener-middleware';
import { startNotificationListen } from './middleware/notification-middleware';
import { startSettingsListen } from './middleware/settings-sync-middleware';
import { startSyncListen } from './middleware/sync-middleware';
import { startWeatherRecordingListener } from './middleware/weather-middleware';
import { startWorkspaceSettingsListener } from './middleware/workspace-settings-sync-middleware';
import { rootReducer, RootState } from './slices';
import { workspaceSlice, WorkspaceStateItem } from './slices/workspace-slice';

startNotificationListen();
startSettingsListen();
startSyncListen();
startDrawingObjectListener();
startWeatherRecordingListener();
startWorkspaceSettingsListener();

export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().prepend(listenerMiddleware.middleware)
});

export type AppStore = typeof store;
export type AppDispatch = AppStore['dispatch'];
export type AppThunk = (dispatch: AppDispatch, getState: () => RootState) => void;
export const createAppAsyncThunk = createAsyncThunk.withTypes<{ state: RootState; dispatch: AppDispatch }>();

export function patchFocusedWorkspaceView(mutator: (view: UdViewModel) => void, targetWorkspaceId?: number) {
    const workspace = store.getState().workspace;
    const workspaceId = targetWorkspaceId === undefined ? workspace.selectedId : targetWorkspaceId;
    if (workspaceId >= workspace.items.length) {
        return workspaceSlice.actions.applyPatchWorkspaceViewView({ patches: [], workspaceId: workspaceId });
    }
    const view = workspace.items[workspaceId].view;
    const [_, patches] = produceWithPatches(view, (v) => {
        mutator(v);
    });
    // Patches contain just pure JS objects without any methods, makes redux happy
    return workspaceSlice.actions.applyPatchWorkspaceViewView({ patches, workspaceId });
}

export function patchFocusedWorkspace(mutator: (item: WorkspaceStateItem) => any) {
    const workspace = store.getState().workspace;
    const workspaceItem = workspace.items[workspace.selectedId];
    const patches = produceWithPatches(workspaceItem, (v) => {
        mutator(v);
    });
    // Patches contain just pure JS objects without any methods, makes redux happy
    return workspaceSlice.actions.applyPatchWorkspaceItem({ workspaceId: workspace.selectedId, patch: patches[1] });
}

export function patchWorkspace(workspaceId: number, mutator: (item: WorkspaceStateItem) => any) {
    const workspaceItem = store.getState().workspace.items[workspaceId];
    const patches = produceWithPatches(workspaceItem, (v) => {
        mutator(v);
    });
    // Patches contain just pure JS objects without any methods, makes redux happy
    return workspaceSlice.actions.applyPatchWorkspaceItem({ workspaceId, patch: patches[1] });
}

export function subscribeStoreWithSelector<T>(selector: (state: RootState) => T, listener: (value: T) => void) {
    let previousValue: T | undefined = undefined;
    return store.subscribe(() => {
        const newValue = selector(store.getState());
        if (newValue !== previousValue) {
            listener(newValue);
        }
        previousValue = newValue;
    });
}
